import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, SafeHtml } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/pdfjs/pdf.worker.min.js';
import {
  ParentActivityService,
  ParentActivityFeedItem,
  ActivityMediaItem,
  LikeSlug,
  ParentActivityLikeEntry,
  ParentActivityComment,
} from './parent-activity.service';
import { ACTIVITY_REACTIONS, DEFAULT_REACTION_ICON } from '../staff/constant/staff-activity.constant';

declare var customToastr: any;

// customToastr is a global loaded by the surrounding shell. If it's ever
// unavailable, calling it directly throws — this guards every call site so a
// missing toast library can never break the feature.
function safeToast(message: string, type: string) {
  try {
    if (typeof customToastr === 'function') {
      customToastr(message, type);
    }
  } catch {
    // Ignore — showing a toast should never be able to break the feature.
  }
}

// Parent Activity — a read/react/comment social feed. Parents cannot create,
// edit, or delete posts (that's staff-only); this ports the staff
// StaffClassActivityComponent's read/react/comment logic 1:1 (visual design
// matches staff exactly per the pasted spec), swapping staffId -> studentId,
// and dropping the composer / edit-post / delete-post pieces entirely since
// parents never get those.
@Component({
  selector: 'app-parent-activity',
  templateUrl: './parent-activity.component.html',
  styleUrls: ['./parent-activity.component.css'],
})
export class ParentActivityComponent implements OnInit, OnChanges {
  @Input() studentId = '';
  @Input() classId = '';
  @Input() studentSwitchTick = 0;

  Math = Math;
  reactions = ACTIVITY_REACTIONS;
  defaultReactionIcon = DEFAULT_REACTION_ICON;

  // Mirrors the mobile app's ActivityData.js checkFeatureStatus() — each flag
  // is an independent BluewingsFeatureName entry in the student's cached
  // featureList (same source parent-login.component.ts reads via
  // storedStudent/currentFeatureMap). Only ACTIVITY_PARENT_COMMENT_GET is a
  // true functional gate (blocks the comment fetch); the rest only hide the
  // affordance that would trigger their API call.
  likeFeatureEnabled = true;
  commentFeatureEnabled = true;
  editCommentFeatureEnabled = true;
  deleteCommentFeatureEnabled = true;
  getCommentFeatureEnabled = true;
  getViewReactionFeatureEnabled = true;

  posts: ParentActivityFeedItem[] = [];
  pageNumber = 1;
  maxPageNumber = 1;
  loading = true;
  loadingMore = false;
  loadError = false;

  private requestId = 0;

  // Reaction picker state (which post's picker is open)
  openReactionPickerId: number | null = null;

  // Reaction list modal state
  showReactionList = false;
  reactionListActivityId: number | null = null;
  reactionListFilter: 'ALL' | LikeSlug = 'ALL';
  reactionStats: { like: number; love: number; laugh: number; celebration: number } = { like: 0, love: 0, laugh: 0, celebration: 0 };
  reactionEntries: ParentActivityLikeEntry[] = [];
  reactionListLoading = false;

  // Comment modal state (centered modal, matching staff's own UI as-is)
  showComments = false;
  commentActivityId: number | null = null;
  comments: ParentActivityComment[] = [];
  commentsLoading = false;
  commentPageNumber = 1;
  commentHasMore = true;
  newComment = '';
  editingCommentId: string | null = null;
  editingCommentText = '';

  // Media lightbox state — images/videos/links all open in-app.
  showLightbox = false;
  lightboxType: 'document' | 'link' = 'document';
  lightboxUrl: string | null = null;
  lightboxSafeUrl: SafeResourceUrl | null = null;

  // Gallery mode — opened from the collage, shows the full post (author,
  // time, text) plus every attached image/video stacked in a scrollable list.
  showGallery = false;
  galleryPost: ParentActivityFeedItem | null = null;
  galleryItems: ActivityMediaItem[] = [];

  // Full-screen preview of a single media item, opened from within the
  // gallery when a specific image/video is clicked.
  showMediaPreview = false;
  previewItem: ActivityMediaItem | null = null;

  constructor(
    private parentActivityApi: ParentActivityService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.checkFeatureStatus();
    this.loadFeed(1);
  }

  // Same storedStudent/featureList lookup parent-login.component.ts uses
  // (currentFeatureMap/isFeatureEnabled) — duplicated here rather than
  // injected because ParentActivityComponent has no access to that
  // component's private state, and featureList itself only ever lives in
  // localStorage, not in a shared service.
  private get storedStudent(): any {
    const raw = localStorage.getItem('studentDetails');
    if (!raw) { return null; }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed[0] || null : null;
    } catch {
      return null;
    }
  }

  private isFeatureEnabled(featureKey: string): boolean {
    const student = this.storedStudent;
    if (student?.isFutureAcademic) {
      return false;
    }
    const list: any[] = student?.featureList || [];
    if (!list.length) {
      return true;
    }
    const entry = list.find(e => e?.BluewingsFeatureName === featureKey);
    return entry?.ActiveStatus === 'ENABLED';
  }

  private checkFeatureStatus(): void {
    this.likeFeatureEnabled = this.isFeatureEnabled('ACTIVITY_PARENT_LIKE');
    this.commentFeatureEnabled = this.isFeatureEnabled('ACTIVITY_PARENT_COMMENT_CREATE');
    this.editCommentFeatureEnabled = this.isFeatureEnabled('ACTIVITY_PARENT_COMMENT_EDIT');
    this.deleteCommentFeatureEnabled = this.isFeatureEnabled('ACTIVITY_PARENT_COMMENT_DELETE');
    this.getCommentFeatureEnabled = this.isFeatureEnabled('ACTIVITY_PARENT_COMMENT_GET');
    this.getViewReactionFeatureEnabled = this.isFeatureEnabled('ACTIVITY_PARENT_LIKE_VIEWS');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['studentSwitchTick'] && !changes['studentSwitchTick'].firstChange) {
      this.posts = [];
      this.pageNumber = 1;
      this.maxPageNumber = 1;
      this.openReactionPickerId = null;
      this.loadFeed(1);
    }
  }

  private ids(): { studentId: string; classId: string } | null {
    if (!this.studentId || !this.classId) { return null; }
    return { studentId: this.studentId, classId: this.classId };
  }

  async loadFeed(page: number) {
    const ids = this.ids();
    if (!ids) {
      this.loading = false;
      this.loadError = true;
      return;
    }
    if (page === 1) {
      this.loading = true;
    } else {
      this.loadingMore = true;
    }
    this.loadError = false;
    const requestId = ++this.requestId;
    try {
      const response = await firstValueFrom(this.parentActivityApi.getActivityData(ids.classId, ids.studentId, page));
      if (requestId !== this.requestId) { return; }
      const responseDetails = response as any;
      if (responseDetails.status && responseDetails.body) {
        const activities: ParentActivityFeedItem[] = responseDetails.body.activities || [];
        this.posts = page === 1 ? activities : [...this.posts, ...activities];
        this.maxPageNumber = responseDetails.body.maxPageNumber || 1;
        this.pageNumber = page;
      } else {
        this.loadError = true;
      }
    } catch (error) {
      if (requestId !== this.requestId) { return; }
      this.loadError = true;
    } finally {
      if (requestId === this.requestId) {
        this.loading = false;
        this.loadingMore = false;
      }
    }
  }

  loadMore() {
    if (this.pageNumber < this.maxPageNumber && !this.loadingMore) {
      this.loadFeed(this.pageNumber + 1);
    }
  }

  postAuthor(post: ParentActivityFeedItem): string {
    return post.createdByUserName || this.translate.instant('staff');
  }

  postAuthorImage(post: ParentActivityFeedItem): string {
    const img = post.createdByUserImage || '';
    return img && !img.startsWith('/images/') ? img : '';
  }

  postBody(post: ParentActivityFeedItem): string {
    return post.activityDescription || '';
  }

  postImages(post: ParentActivityFeedItem): ActivityMediaItem[] {
    return post.media?.images || [];
  }

  postVideos(post: ParentActivityFeedItem): ActivityMediaItem[] {
    return post.media?.videos || [];
  }

  postDocuments(post: ParentActivityFeedItem): ActivityMediaItem[] {
    return post.media?.documents || [];
  }

  // mediaUrl -> data URL of the rendered first page, or null while pending/failed.
  private pdfThumbCache = new Map<string, string | null>();

  isPdfDocument(doc: ActivityMediaItem): boolean {
    return /\.pdf(\?|$)/i.test(doc.mediaUrl) || /pdf/i.test(doc.mediaSubType || '');
  }

  pdfThumbnail(mediaUrl: string): string | null {
    if (!this.pdfThumbCache.has(mediaUrl)) {
      this.pdfThumbCache.set(mediaUrl, null);
      this.renderPdfThumbnail(mediaUrl);
    }
    return this.pdfThumbCache.get(mediaUrl) || null;
  }

  private async renderPdfThumbnail(mediaUrl: string) {
    try {
      const pdf = await pdfjsLib.getDocument(mediaUrl).promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 0.6 });
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const context = canvas.getContext('2d');
      if (!context) { return; }
      await page.render({ canvasContext: context, viewport }).promise;
      this.pdfThumbCache.set(mediaUrl, canvas.toDataURL('image/png'));
    } catch {
      // Leave the cache entry as null — falls back to the generic file chip.
    }
  }

  hasMedia(post: ParentActivityFeedItem): boolean {
    return this.postImages(post).length > 0 || this.postVideos(post).length > 0 || this.postDocuments(post).length > 0;
  }

  // Collage grid styling only kicks in when there's more than one visual
  // media item — a single image/video renders full-width instead.
  isCollage(post: ParentActivityFeedItem): boolean {
    return (this.postImages(post).length + this.postVideos(post).length) > 1;
  }

  visualMediaCount(post: ParentActivityFeedItem): number {
    return this.postImages(post).length + this.postVideos(post).length;
  }

  // All images+videos for a post, in display order — the collage tiles show
  // at most the first 4 of these, and the gallery browses the full list.
  allVisualMedia(post: ParentActivityFeedItem): ActivityMediaItem[] {
    return [...this.postImages(post), ...this.postVideos(post)];
  }

  // Number hidden behind the "+N" overlay on the last visible collage tile.
  extraMediaCount(post: ParentActivityFeedItem): number {
    return Math.max(0, this.visualMediaCount(post) - 4);
  }

  openGallery(post: ParentActivityFeedItem) {
    this.galleryPost = post;
    this.galleryItems = this.allVisualMedia(post);
    this.showGallery = true;
  }

  closeGallery() {
    this.showGallery = false;
    this.galleryPost = null;
  }

  openMediaPreview(item: ActivityMediaItem, event?: Event) {
    event?.stopPropagation();
    this.previewItem = item;
    this.showMediaPreview = true;
  }

  closeMediaPreview() {
    this.showMediaPreview = false;
    this.previewItem = null;
  }

  // Emoji for every distinct reaction type present on the post (e.g. both
  // ❤️ and 🎉 if different people reacted differently), shown stacked
  // before the "X and N others reacted" summary text.
  reactionIcons(post: ParentActivityFeedItem): string[] {
    const types = post.uniqueFeedLikeTypes || [];
    return this.reactions.filter(r => types.includes(r.slug as string)).map(r => r.icon);
  }

  reactionSummaryText(post: ParentActivityFeedItem): string {
    const count = this.postTotalLikes(post);
    if (!count) { return ''; }
    const lastName = post.lastLikedByUserName;
    if (!lastName) { return `${count} reaction${count === 1 ? '' : 's'}`; }
    const others = count - 1;
    if (others <= 0) { return `${lastName} reacted`; }
    return `${lastName} and ${others} other${others === 1 ? '' : 's'} reacted`;
  }

  // Detects a bare URL inside the post text so it can be shown as an
  // in-app-only link preview instead of raw text with a real <a> navigation.
  private readonly urlPattern = /(https?:\/\/[^\s]+)/i;
  private readonly youtubePattern = /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{6,})/i;

  postLink(post: ParentActivityFeedItem): string | null {
    const match = (post.activityDescription || '').match(this.urlPattern);
    return match ? match[0] : null;
  }

  postTextWithoutLink(post: ParentActivityFeedItem): string {
    const link = this.postLink(post);
    return link ? this.postBody(post).replace(link, '').trim() : this.postBody(post);
  }

  private readonly hashtagPattern = /(^|\s)(#[a-zA-Z0-9_]+)/g;

  // Escapes the raw text first (so nothing in activityDescription can inject
  // markup), then wraps #hashtags in <strong> for bold rendering.
  postBodyHtml(post: ParentActivityFeedItem): SafeHtml {
    const escaped = this.escapeHtml(this.postTextWithoutLink(post));
    const bolded = escaped.replace(this.hashtagPattern, (_match, lead, tag) => `${lead}<strong>${tag}</strong>`);
    return this.sanitizer.bypassSecurityTrustHtml(bolded);
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // Real thumbnail via YouTube's public static thumbnail URL pattern — no
  // API key or metadata fetch needed. Other links fall back to a generic
  // domain-only card since there's no OpenGraph source available client-side.
  linkPreview(url: string): { isYouTube: boolean; thumbnail: string; domain: string } {
    const ytMatch = url.match(this.youtubePattern);
    const domain = this.extractDomain(url);
    if (ytMatch) {
      return { isYouTube: true, thumbnail: `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`, domain };
    }
    return { isYouTube: false, thumbnail: '', domain };
  }

  private extractDomain(url: string): string {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  }

  postCommentCount(post: ParentActivityFeedItem): number {
    return post.commentsCount ?? 0;
  }

  postTotalLikes(post: ParentActivityFeedItem): number {
    return post.likesCount ?? 0;
  }

  isReactedByMe(post: ParentActivityFeedItem, slug: LikeSlug): boolean {
    return post.userLikedStatus === slug;
  }

  // Emoji of the user's own reaction, if any — used on the React button so
  // it reflects what they picked instead of always showing the thumb icon.
  myReactionIcon(post: ParentActivityFeedItem): string | null {
    const reaction = this.reactions.find(r => r.slug === post.userLikedStatus);
    return reaction ? reaction.icon : null;
  }

  isOwnComment(comment: ParentActivityComment): boolean {
    return this.studentId != null && String(comment.commentByUserId) === String(this.studentId);
  }

  canEditComment(comment: ParentActivityComment): boolean {
    return this.editCommentFeatureEnabled && this.isOwnComment(comment);
  }

  canDeleteComment(comment: ParentActivityComment): boolean {
    return this.deleteCommentFeatureEnabled && this.isOwnComment(comment);
  }

  // ---------- Likes / reactions ----------
  private reactionPickerCloseTimer: any = null;

  toggleReactionPicker(post: ParentActivityFeedItem) {
    this.openReactionPickerId = this.openReactionPickerId === post.activityId ? null : post.activityId;
  }

  openReactionPickerOnHover(post: ParentActivityFeedItem) {
    clearTimeout(this.reactionPickerCloseTimer);
    this.openReactionPickerId = post.activityId;
  }

  scheduleReactionPickerClose() {
    clearTimeout(this.reactionPickerCloseTimer);
    this.reactionPickerCloseTimer = setTimeout(() => {
      this.openReactionPickerId = null;
    }, 300);
  }

  cancelReactionPickerClose() {
    clearTimeout(this.reactionPickerCloseTimer);
  }

  async reactToPost(post: ParentActivityFeedItem, slug: LikeSlug) {
    const ids = this.ids();
    if (!ids) { return; }
    this.openReactionPickerId = null;
    const alreadyReacted = this.isReactedByMe(post, slug);
    const newSlug: LikeSlug = alreadyReacted ? null : slug;
    const wasLiked = post.userLikedStatus !== null;
    try {
      const response = await firstValueFrom(this.parentActivityApi.likePost({
        lang: 'en',
        classId: ids.classId,
        studentId: ids.studentId,
        activityId: String(post.activityId),
        likeSlug: newSlug,
      }));
      const responseDetails = response as any;
      if (responseDetails.status && responseDetails.body) {
        post.uniqueFeedLikeTypes = responseDetails.body.uniqueLikeTypes || [];
        post.userLikedStatus = newSlug;
        const nowLiked = newSlug !== null;
        if (wasLiked && !nowLiked) {
          post.likesCount = Math.max(post.likesCount - 1, 0);
        } else if (!wasLiked && nowLiked) {
          post.likesCount = post.likesCount + 1;
        }
      } else {
        safeToast(responseDetails.message || await firstValueFrom(this.translate.get('somethingWentWrong')), 'error');
      }
    } catch (error) {
      safeToast(await firstValueFrom(this.translate.get('staffSomethingWentWrong')), 'error');
    }
  }

  async openReactionList(post: ParentActivityFeedItem) {
    if (!this.getViewReactionFeatureEnabled) { return; }
    const ids = this.ids();
    if (!ids) { return; }
    this.reactionListActivityId = post.activityId;
    this.reactionListFilter = 'ALL';
    this.showReactionList = true;
    await this.loadReactionList(1);
  }

  async loadReactionList(page: number) {
    const ids = this.ids();
    if (!ids || !this.reactionListActivityId) { return; }
    this.reactionListLoading = true;
    try {
      const response = await firstValueFrom(
        this.parentActivityApi.getReactions(ids.classId, ids.studentId, String(this.reactionListActivityId), this.reactionListFilter, page)
      );
      const responseDetails = response as any;
      if (responseDetails.status && responseDetails.body) {
        this.reactionStats = responseDetails.body.activityLikesStat;
        this.reactionEntries = page === 1 ? responseDetails.body.likes : [...this.reactionEntries, ...responseDetails.body.likes];
      }
    } catch (error) {
      safeToast(await firstValueFrom(this.translate.get('couldNotLoadReactions')), 'error');
    } finally {
      this.reactionListLoading = false;
    }
  }

  // Chips with zero count are hidden per the parent spec (unlike the staff
  // reference, which shows all chips regardless of count).
  hasReactionsOfType(slug: 'like' | 'love' | 'laugh' | 'celebration'): boolean {
    return (this.reactionStats?.[slug] || 0) > 0;
  }

  setReactionFilter(filter: 'ALL' | LikeSlug) {
    this.reactionListFilter = filter;
    this.loadReactionList(1);
  }

  closeReactionList() {
    this.showReactionList = false;
  }

  // ---------- Comments ----------
  async openCommentSheet(post: ParentActivityFeedItem) {
    this.commentActivityId = post.activityId;
    this.comments = [];
    this.commentPageNumber = 1;
    this.commentHasMore = true;
    this.newComment = '';
    this.showComments = true;
    await this.loadComments(1);
  }

  async loadComments(page: number) {
    // The one true functional gate among the ACTIVITY_PARENT_* flags — this
    // blocks the API call itself, not just the affordance, matching the
    // mobile app's getComments() early-return.
    if (!this.getCommentFeatureEnabled) { return; }
    const ids = this.ids();
    if (!ids || !this.commentActivityId) { return; }
    this.commentsLoading = true;
    try {
      const response = await firstValueFrom(this.parentActivityApi.getActivityComment(ids.classId, ids.studentId, String(this.commentActivityId), page));
      const responseDetails = response as any;
      if (responseDetails.status && responseDetails.body) {
        const newComments: ParentActivityComment[] = responseDetails.body.activityComments || [];
        this.comments = page === 1 ? newComments : [...this.comments, ...newComments];
        this.commentPageNumber = page;
        this.commentHasMore = newComments.length >= 10;
      }
    } catch (error) {
      safeToast(await firstValueFrom(this.translate.get('couldNotLoadComments')), 'error');
    } finally {
      this.commentsLoading = false;
    }
  }

  loadMoreComments() {
    if (this.commentHasMore && !this.commentsLoading) {
      this.loadComments(this.commentPageNumber + 1);
    }
  }

  closeCommentSheet() {
    this.showComments = false;
    this.editingCommentId = null;
  }

  async submitComment() {
    const ids = this.ids();
    if (!ids || !this.commentActivityId || !this.newComment.trim()) { return; }
    try {
      const response = await firstValueFrom(this.parentActivityApi.doComment({
        lang: 'en',
        classId: ids.classId,
        studentId: ids.studentId,
        activityId: String(this.commentActivityId),
        comment: this.newComment.trim(),
      }));
      const responseDetails = response as any;
      if (responseDetails.status) {
        this.newComment = '';
        this.loadComments(1);
        const post = this.posts.find(p => p.activityId === this.commentActivityId);
        if (post) {
          post.commentsCount = (post.commentsCount ?? 0) + 1;
        }
      } else {
        safeToast(responseDetails.message || await firstValueFrom(this.translate.get('somethingWentWrong')), 'error');
      }
    } catch (error) {
      safeToast(await firstValueFrom(this.translate.get('staffSomethingWentWrong')), 'error');
    }
  }

  startEditComment(comment: ParentActivityComment) {
    if (!this.canEditComment(comment)) { return; }
    this.editingCommentId = comment.commentId;
    this.editingCommentText = comment.comment;
  }

  cancelEditComment() {
    this.editingCommentId = null;
    this.editingCommentText = '';
  }

  async saveEditedComment() {
    const ids = this.ids();
    if (!ids || !this.editingCommentId || !this.editingCommentText.trim()) { return; }
    const editingComment = this.comments.find(c => c.commentId === this.editingCommentId);
    if (!editingComment || !this.canEditComment(editingComment)) { return; }
    try {
      const response = await firstValueFrom(this.parentActivityApi.editActivityComment({
        lang: 'en',
        classId: ids.classId,
        studentId: ids.studentId,
        comment: this.editingCommentText.trim(),
        commentId: this.editingCommentId,
      }));
      const responseDetails = response as any;
      if (responseDetails.status) {
        const comment = this.comments.find(c => c.commentId === this.editingCommentId);
        if (comment) {
          comment.comment = this.editingCommentText.trim();
        }
        this.cancelEditComment();
      } else {
        safeToast(await firstValueFrom(this.translate.get('somethingWentWrong')), 'error');
      }
    } catch (error) {
      safeToast(await firstValueFrom(this.translate.get('staffSomethingWentWrong')), 'error');
    }
  }

  // Delete-comment confirm modal state.
  showDeleteCommentModal = false;
  deletingComment = false;
  private deleteCommentTarget: ParentActivityComment | null = null;

  deleteComment(comment: ParentActivityComment) {
    if (!this.canDeleteComment(comment)) { return; }
    this.deleteCommentTarget = comment;
    this.showDeleteCommentModal = true;
  }

  closeDeleteCommentModal() {
    if (this.deletingComment) { return; }
    this.showDeleteCommentModal = false;
    this.deleteCommentTarget = null;
  }

  async confirmDeleteComment() {
    if (!this.deleteCommentTarget || this.deletingComment) { return; }
    const comment = this.deleteCommentTarget;

    const ids = this.ids();
    if (!ids) { return; }
    this.deletingComment = true;
    try {
      const response = await firstValueFrom(this.parentActivityApi.deleteComment({
        commentId: comment.commentId,
        studentId: ids.studentId,
        classId: ids.classId,
        lang: 'en',
      }));
      const responseDetails = response as any;
      if (responseDetails.status) {
        this.comments = this.comments.filter(c => c.commentId !== comment.commentId);
        const post = this.posts.find(p => p.activityId === this.commentActivityId);
        if (post) {
          post.commentsCount = Math.max((post.commentsCount ?? 1) - 1, 0);
        }
      } else {
        safeToast(await firstValueFrom(this.translate.get('somethingWentWrong')), 'error');
      }
    } catch (error) {
      safeToast(await firstValueFrom(this.translate.get('staffSomethingWentWrong')), 'error');
    } finally {
      this.deletingComment = false;
      this.showDeleteCommentModal = false;
      this.deleteCommentTarget = null;
    }
  }

  // ---------- In-app media / link lightbox ----------
  // Every media type and any link found in a post opens here instead of
  // navigating away — nothing should ever leave the parent portal.
  openDocumentLightbox(url: string) {
    this.lightboxType = 'document';
    this.lightboxUrl = url;
    this.lightboxSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.showLightbox = true;
  }

  openLinkLightbox(url: string) {
    this.lightboxType = 'link';
    this.lightboxUrl = url;
    this.lightboxSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.showLightbox = true;
  }

  closeLightbox() {
    this.showLightbox = false;
    this.lightboxSafeUrl = null;
    this.lightboxUrl = null;
  }
}
